import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';

import PageTitle from '../components/generic/PageTitle';
import QuickActionCard from '../components/generic/QuickActions';
import MatchmakerLoader from 'components/requirements/MatchmakerLoader';
import EquipmentMatchListings from 'components/requirements/EquipmentMatchListings';
import EquipmentChecklist from 'components/requirements/EquipmentChecklist';
import Breadcrumbs from 'components/generic/Breadcrumbs';

import { postAssignEquipments, postEquipmentMatch } from 'lib/api/requirementsApi';

const SelectEquipmentPage = () => {
  const formRef = useRef(null);
  const navigate = useNavigate();
  const { state } = useLocation();
  const location = useLocation();

  // Extract values passed from previous page
  const { project_id, equipment_needed, selectedCrew, event_date } = location.state || {};

  const {
    currentProject,
    assignEquipment,
    loading,
    error
  } = useProject();

  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState([]);
  const [isMatching, setIsMatching] = useState(true);
  const [matchError, setMatchError] = useState(null);

  const [crewNeedEquipments, setCrewNeedEquipments] = useState([]);
  const [crewEquipments, setCrewEquipments] = useState([]);

  useEffect(() => {
    const runMatch = async () => {
      if (!currentProject) {
        navigate('/requirements');
        return;
      }

      setIsMatching(true);
      setMatchError(null);

      try {
        const parsedNeeds = equipment_needed
          ? equipment_needed : [];

        const result = await postEquipmentMatch({
          equipments_needed: parsedNeeds,
          crew_id: selectedCrew || [],
          event_date: event_date,
        });

        if (result.error) {
          setMatchError(result.error || 'Failed to match equipment');
        } else {
          // Extract all needs_pick from all crew entries
          const allNeedsPick = result.data.flatMap(entry => entry.needs_pick || []);
          const uniqueNeedsPick = Object.values(
            allNeedsPick.reduce((acc, item) => {
              acc[item.equipment_id] = item;  // override duplicates by id
              return acc;
            }, {})
          );

          setCrewNeedEquipments(uniqueNeedsPick);

          const allHasEquip = result.data.flatMap(entry => entry.has || []);
          setCrewEquipments(allHasEquip);
        }

      } catch (err) {
        console.error("Equipment match API failed", err);
        setMatchError("Unexpected error matching equipment");
      }

      setIsMatching(false);
    };

    runMatch();
  }, [currentProject, navigate, equipment_needed, selectedCrew]);

  const handleAssignEquipment = async () => {
    if (selectedEquipmentIds.length === 0) {
      setMatchError('Please select at least one equipment item');
      return;
    }

    if (!currentProject?.id) {
      setMatchError('No project selected');
      return;
    }

    const result = await postAssignEquipments({
      project_id: project_id,
      assigned_equipment: selectedEquipmentIds,
    });

    if (!result.error) {
      navigate('/requirements/ai-matchmaker/select-equipment/project-brief', {
        state: {
          project_id: project_id,
          assigned_equipment: selectedEquipmentIds,
          assigned_crew: selectedCrew
        }
      });
    } else {
      setMatchError(result.error || 'Failed to assign equipment');
    }
  };

  const handleEquipmentSelection = (equipmentId, isSelected) => {
    const item = crewNeedEquipments.find(e => e.equipment_id === equipmentId);

    if (isSelected && item && item.is_available === 0) {
      return;
    }

    setSelectedEquipmentIds(prev =>
      isSelected
        ? [...prev, equipmentId]
        : prev.filter(id => id !== equipmentId)
    );
  };

  return (
    <div className="selectt-equipments-content p-6">
      <Breadcrumbs />

      <>
        <PageTitle
          title={"Select Equipment"}
          description={
            currentProject
              ? `Choose the equipment needed for "${currentProject?.project_name}"`
              : "Choose the equipment needed for your livestream project."
          }
        />

        {/* ERROR DISPLAY */}
        {(matchError || error) && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {matchError || error}
          </div>
        )}

        {/* LOADING SPINNER */}
        {isMatching ? (
          <MatchmakerLoader />
        ) : (
          <div className="flex flex-col md:flex-row gap-6 md:self-stretch">
            <div className="md:w-3/4">
              <EquipmentMatchListings
                matchedEquipment={crewNeedEquipments} // This now contains the is_available property
                crewEquipments={crewEquipments}
                selectedEquipmentIds={selectedEquipmentIds}
                onEquipmentSelection={handleEquipmentSelection}
              />
            </div>

            <div className="md:w-1/4">
              <div className="sticky top-6 flex flex-col justify-start items-start gap-6">
                <QuickActionCard
                  handleClick={handleAssignEquipment}
                  buttonText={`Assign Equipment (${String(
                    selectedEquipmentIds.length
                  ).padStart(2, "0")})`}
                  disabled={loading || selectedEquipmentIds.length === 0}
                />
                <EquipmentChecklist
                  hasItems={crewEquipments}
                  needsItems={crewNeedEquipments}
                  selectedIds={selectedEquipmentIds}
                />
              </div>
            </div>
          </div>
        )}
      </>
    </div>
  );
};

export default SelectEquipmentPage;
